import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CATEGORY_COLOURS,
  paletteCssVariables,
  resolveColour,
  type CategoryColour,
} from '@fint/validation';
import { cn } from '@/lib/cn';

/**
 * The ten palette tokens, written into the document from the ONE table that a
 * test measures - `PALETTE_TOKENS` in @fint/validation.
 *
 * They used to be hand-written into `globals.css` beneath a comment asserting
 * they all cleared 4.5:1, which amber did not. Two copies of a number, one of
 * them a claim nothing could check. Mounted once, at the top of the app.
 *
 * Bare `:root` for the light values and `.dark` after it, at equal specificity,
 * so source order decides - and the order is fixed by being one string. It was
 * `:root:not(.dark)`, which reads as tidier but is wrong the moment the dark
 * rule is scoped to the screen: in print, with the class still on <html>,
 * NEITHER rule matched and every chip lost its colour.
 *
 * The dark rule is scoped to the screen, as the dark block in globals.css is:
 * the invoice page is printed from the browser, and a printout must be light
 * whatever theme is on screen. Without this the chips would print on a white
 * page in the dark theme's pale tints, most of them under 2:1.
 */
export function PaletteStyles() {
  return (
    <style>{`:root { ${paletteCssVariables('light')} } @media not print { .dark { ${paletteCssVariables('dark')} } }`}</style>
  );
}

/**
 * Rendering the classification palette.
 *
 * ── WHY THESE ARE INLINE STYLES AND NOT TAILWIND CLASSES ──
 * The colour is DATA - it arrives on a row at runtime - so the class name would
 * have to be built by concatenation, and Tailwind's scanner reads source text
 * rather than running it. `bg-cat-${colour}/12` compiles to nothing at all and
 * the chip renders transparent, which looks like a CSS bug rather than a build
 * one. Referencing the CSS variable directly keeps the theme in charge and
 * cannot be tree-shaken away.
 *
 * ── COLOUR IS NEVER THE ONLY CARRIER OF MEANING ──
 * Every component here renders the NAME beside the colour, and the dot is
 * `aria-hidden` - announcing "blue circle" before every tag name is noise, and
 * a tag rendered as a bare dot is unreadable to about one man in twelve.
 */

/** The CSS variable for a slot, resolved fail-soft. */
function colourVar(colour: string | null | undefined): string {
  return `var(--cat-${resolveColour(colour)})`;
}

/** Full strength - a bar, a chart series, a dot. Exported for the one control drawn outside this file, the Inbox's book buttons. */
export const colourSolid = (colour: string | null | undefined) => `hsl(${colourVar(colour)})`;
/** The 12% tint a chip sits on. */
export const colourTint = (colour: string | null | undefined) => `hsl(${colourVar(colour)} / 0.12)`;

export function ColourDot({ colour, className }: { colour: string | null | undefined; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn('inline-block h-1.5 w-1.5 shrink-0 rounded-full', className)}
      style={{ backgroundColor: colourSolid(colour) }}
    />
  );
}

/**
 * A category's emoji, or its colour dot when it has none.
 *
 * ── THE EMOJI STANDS WHERE THE DOT STOOD ──
 * Both are find-aids for the same thing; a dot AND a picture before every
 * name is two markers for one fact, and the chip gets busy. The tint behind
 * the chip still carries the colour, so nothing is lost when the picture
 * takes the dot's place. `aria-hidden` for the same reason the dot is: the
 * name is the accessible text, and "fork and knife with plate Meals" is
 * noise before every row.
 */
export function CategoryMark({
  emoji,
  colour,
  className,
}: {
  emoji: string | null | undefined;
  colour: string | null | undefined;
  className?: string;
}) {
  if (emoji) {
    return (
      <span aria-hidden="true" className={cn('shrink-0 leading-none', className)}>
        {emoji}
      </span>
    );
  }
  return <ColourDot colour={colour} />;
}

/**
 * A tag or a category, as a chip.
 *
 * Reuses `Badge`'s geometry so one sits correctly beside a `StatusBadge`, and
 * always prints the name: the colour is a find-aid, never an identifier.
 * `emoji`, when a category has one, stands in for the dot - see `CategoryMark`.
 */
export function ColourChip({
  colour,
  name,
  emoji,
  onRemove,
  className,
}: {
  colour: string | null | undefined;
  name: string;
  emoji?: string | null;
  onRemove?: () => void;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium',
        className,
      )}
      style={{ backgroundColor: colourTint(colour), color: colourSolid(colour) }}
    >
      <CategoryMark emoji={emoji} colour={colour} />
      {name}
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          // Named for what it does to WHICH tag, because a row of five "Remove"
          // buttons is five identical announcements.
          aria-label={`Remove tag ${name}`}
          className="-mr-0.5 rounded-full px-0.5 leading-none hover:opacity-70"
        >
          ×
        </button>
      ) : null}
    </span>
  );
}

/**
 * The ten swatches.
 *
 * The current one is marked with a CHECK GLYPH rather than a ring alone - a
 * ring is a colour cue on a colour control, which is the one place it cannot
 * be the only signal.
 */
export function ColourPicker({
  value,
  onChange,
  label = 'Colour',
}: {
  value: CategoryColour;
  onChange: (colour: CategoryColour) => void;
  label?: string;
}) {
  const container = useRef<HTMLDivElement>(null);

  /**
   * ── ROVING TABINDEX, AND WHY A RADIOGROUP NEEDS ONE ──
   * Ten `role="radio"` buttons each carrying tabindex 0 is ten tab stops for
   * one decision, and the arrow keys - which is how anybody using a keyboard
   * actually moves through a radio group - did nothing at all. That is not a
   * radio group with a rough edge; it is a radiogroup role over controls that
   * do not behave like one, which is worse than no role.
   *
   * So: exactly one stop, on the selected swatch, and the arrows move the
   * selection and the focus together, wrapping at both ends.
   */
  const move = (delta: number) => {
    const index = CATEGORY_COLOURS.indexOf(value);
    const next = CATEGORY_COLOURS[(index + delta + CATEGORY_COLOURS.length) % CATEGORY_COLOURS.length];
    if (!next) return;
    onChange(next);
    container.current?.querySelector<HTMLButtonElement>(`[data-colour="${next}"]`)?.focus();
  };

  return (
    <div
      ref={container}
      role="radiogroup"
      aria-label={label}
      className="flex flex-wrap gap-1.5"
      onKeyDown={(event) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          move(1);
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault();
          move(-1);
        }
      }}
    >
      {CATEGORY_COLOURS.map((colour) => (
        <button
          key={colour}
          type="button"
          role="radio"
          data-colour={colour}
          aria-checked={value === colour}
          tabIndex={value === colour ? 0 : -1}
          aria-label={`Colour: ${colour[0].toUpperCase()}${colour.slice(1)}`}
          onClick={() => onChange(colour)}
          className={cn(
            // primary-foreground, not white: it is near-white in the light
            // theme, where the swatches are dark, and navy in the dark theme,
            // where they are light. A white check on dark-theme amber measured
            // 2.2:1 - the selected swatch was the one you could not read.
            // 44px under a finger: ten 28px discs at 6px apart are a control where
            // the miss lands on the neighbour, which is the wrong colour.
            'flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-primary-foreground touch:h-11 touch:w-11 touch:text-sm',
            value === colour ? 'ring-2 ring-ring ring-offset-2 ring-offset-background' : '',
          )}
          /*
            FULL STRENGTH, not the 12% tint a chip uses. The picker's entire job
            is showing which colour is which, and ten swatches at 12% opacity
            are ten near-identical pale discs - the one control where the tint
            defeats the purpose.
          */
          style={{ backgroundColor: colourSolid(colour) }}
        >
          {/* A check glyph, not a ring alone: a ring is a colour cue on a
              colour control, which is the one place it cannot be the only
              signal. The ring is kept as the FOCUS indicator. */}
          {value === colour ? '✓' : ''}
        </button>
      ))}
    </div>
  );
}

/**
 * A ranked horizontal bar list - the breakdown, and deliberately not a pie.
 *
 * The question a reader has is "what is my biggest cost, and how much bigger is
 * it than the next one". That is a comparison of magnitudes, and magnitudes are
 * read accurately as lengths on a common baseline and badly as angles. Eight
 * slices is also past the point where a pie can be labelled without a legend,
 * and a legend forces the eye between two places to answer one question; a bar
 * row puts the name, the length and the value on one line.
 *
 * It needs no chart library: a div with a percentage width, themed off the
 * row's own palette token. It works at any width, on a phone, and in print, and
 * it cannot be distorted the way a tilted 3-D disc is.
 *
 * The bar itself is `aria-hidden`; the row's accessible text is the name, the
 * amount and the share, which is the whole fact.
 */
export interface RankedBarRow {
  id: string;
  name: string;
  colour: string;
  emoji?: string | null;
  amount: string;
  share: number;
  /** A second line under the name: the HSN/SAC, "typed line", "no vendor named". */
  meta?: string | null;
  /** Where the row goes when clicked. A row without one is inert. */
  href?: string;
  /**
   * What the row says when hovered, focused or tapped: the card's body.
   * Rendered inside the row, so it can be anything - a list of facts, a
   * comparison, the click's own label.
   */
  detail?: React.ReactNode;
  /** Shown greyed and outside the shares: a category marked out of the totals. */
  excluded?: boolean;
}

export function RankedBars({
  rows,
  renderValue,
  emptyLabel = 'Nothing in this period.',
}: {
  rows: RankedBarRow[];
  renderValue: (amount: string) => React.ReactNode;
  emptyLabel?: string;
}) {
  // The row whose card is open. One at a time: a hover moves it, a focus
  // moves it, a tap on a phone toggles it (and a second tap follows the link).
  const [active, setActive] = useState<string | null>(null);
  if (rows.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">{emptyLabel}</p>;
  }
  // Scaled against the LARGEST row, not against the total: at eight rows a
  // share-scaled bar leaves every row after the second a sliver, and the
  // comparison the chart exists for stops being readable.
  const largest = rows.reduce((max, row) => Math.max(max, Math.abs(Number(row.amount))), 0) || 1;

  return (
    <ul className="space-y-2.5" data-ranked-bars>
      {rows.map((row) => {
        const open = active === row.id && !!row.detail;
        const body = (
          <>
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="flex min-w-0 items-center gap-1.5">
                <CategoryMark emoji={row.emoji} colour={row.colour} />
                {/* `min-w-0` on the truncating span as well as its parent: a
                    flex item's minimum is its full text, so without it "Meals
                    and entertainment" set the card's minimum width and pushed
                    the dashboard 12px past a 320px screen. */}
                <span className={cn('min-w-0 truncate', row.excluded && 'text-muted-foreground')}>{row.name}</span>
                {row.meta ? <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">· {row.meta}</span> : null}
              </span>
              <span className={cn('shrink-0 tabular-nums', row.excluded && 'text-muted-foreground')}>
                {renderValue(row.amount)}
                <span className="ml-2 text-xs text-muted-foreground">{row.excluded ? 'not counted' : `${(row.share * 100).toFixed(0)}%`}</span>
              </span>
            </div>
            <div aria-hidden="true" className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.max(2, (Math.abs(Number(row.amount)) / largest) * 100)}%`,
                  backgroundColor: colourSolid(row.colour),
                  opacity: row.excluded ? 0.4 : 1,
                }}
              />
            </div>
          </>
        );
        const interactive = !!row.href || !!row.detail;
        return (
          <li
            key={row.id}
            className="relative"
            onPointerEnter={(event) => {
              if (event.pointerType !== 'touch' && row.detail) setActive(row.id);
            }}
            onPointerLeave={(event) => {
              if (event.pointerType !== 'touch') setActive((current) => (current === row.id ? null : current));
            }}
          >
            {row.href ? (
              <Link
                to={row.href}
                // A thumb's height on a touch screen: the row is the link.
                className={cn('block space-y-1 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring touch:flex touch:min-h-11 touch:flex-col touch:justify-center', interactive && '-mx-1.5 px-1.5 py-0.5 hover:bg-accent/60')}
                aria-describedby={open ? `${row.id}-detail` : undefined}
                onFocus={() => setActive(row.id)}
                onBlur={() => setActive((current) => (current === row.id ? null : current))}
                onClick={(event) => {
                  // On a phone the first tap opens the card; the link inside it, or a second tap, follows.
                  if (row.detail && matchMedia('(pointer: coarse)').matches && active !== row.id) {
                    event.preventDefault();
                    setActive(row.id);
                  }
                }}
                data-ranked-row
              >
                {body}
              </Link>
            ) : (
              <div
                className={cn('space-y-1', row.detail && '-mx-1.5 rounded-md px-1.5 py-0.5 touch:flex touch:min-h-11 touch:flex-col touch:justify-center')}
                tabIndex={row.detail ? 0 : undefined}
                aria-describedby={open ? `${row.id}-detail` : undefined}
                onFocus={() => setActive(row.id)}
                onBlur={() => setActive((current) => (current === row.id ? null : current))}
                onClick={() => {
                  if (row.detail) setActive((current) => (current === row.id ? null : row.id));
                }}
                data-ranked-row
              >
                {body}
              </div>
            )}
            {open ? (
              <div
                id={`${row.id}-detail`}
                role="tooltip"
                // `pointer-events-none`: the card sits over the rows below, and
                // the pointer must reach them - otherwise the next row cannot be
                // hovered without leaving the list and coming back.
                className="pointer-events-none absolute left-0 top-full z-20 mt-1 w-max max-w-[min(340px,100%)] rounded-md border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md"
                data-ranked-detail
              >
                {row.detail}
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
